"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiClient = exports.ApiRequestError = void 0;
exports.createApiClient = createApiClient;
class ApiRequestError extends Error {
    statusCode;
    body;
    constructor(statusCode, body, message) {
        super(message);
        this.statusCode = statusCode;
        this.body = body;
        this.name = "ApiRequestError";
    }
}
exports.ApiRequestError = ApiRequestError;
class ApiClient {
    opts;
    refreshing = null;
    constructor(opts) {
        this.opts = opts;
    }
    /* ------------------------------------------------------------------ */
    /* Core request machinery                                             */
    /* ------------------------------------------------------------------ */
    get fetchFn() {
        return this.opts.fetchFn ?? fetch;
    }
    buildUrl(path, query) {
        let url = `${this.opts.baseUrl}${path}`;
        if (query) {
            const params = new URLSearchParams();
            for (const [k, v] of Object.entries(query)) {
                if (v !== undefined && v !== "")
                    params.set(k, String(v));
            }
            const qs = params.toString();
            if (qs)
                url += `?${qs}`;
        }
        return url;
    }
    async request(method, path, options = {}) {
        const doFetch = async () => {
            const headers = {};
            const token = await this.opts.getAccessToken?.();
            if (token)
                headers["Authorization"] = `Bearer ${token}`;
            let body;
            if (options.formData) {
                body = options.formData;
            }
            else if (options.body !== undefined) {
                headers["Content-Type"] = "application/json";
                body = JSON.stringify(options.body);
            }
            return this.fetchFn(this.buildUrl(path, options.query), {
                method,
                headers,
                body,
                credentials: "include",
            });
        };
        let res = await doFetch();
        if (res.status === 401 && !options.skipRefresh) {
            const refreshed = await this.tryRefresh();
            if (refreshed) {
                res = await doFetch();
            }
            else {
                await this.opts.onUnauthorized?.();
            }
        }
        if (!res.ok) {
            let parsed = null;
            try {
                parsed = (await res.json());
            }
            catch {
                /* non-JSON error body */
            }
            throw new ApiRequestError(res.status, parsed, parsed?.message ?? `Request failed (${res.status})`);
        }
        if (options.raw)
            return res;
        if (res.status === 204)
            return undefined;
        return (await res.json());
    }
    /** Single-flight refresh: concurrent 401s share one refresh call. */
    tryRefresh() {
        if (!this.refreshing) {
            this.refreshing = (async () => {
                try {
                    const refreshToken = (await this.opts.getRefreshToken?.()) ?? undefined;
                    const res = await this.fetchFn(this.buildUrl("/api/auth/refresh"), {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ refreshToken }),
                        credentials: "include",
                    });
                    if (!res.ok)
                        return false;
                    const data = (await res.json());
                    if (data.accessToken && data.refreshToken) {
                        await this.opts.onTokens?.({
                            accessToken: data.accessToken,
                            refreshToken: data.refreshToken,
                        });
                    }
                    return true;
                }
                catch {
                    return false;
                }
                finally {
                    // allow the next refresh after this one settles
                    setTimeout(() => (this.refreshing = null), 0);
                }
            })();
        }
        return this.refreshing;
    }
    /* ------------------------------------------------------------------ */
    /* Auth                                                                */
    /* ------------------------------------------------------------------ */
    auth = {
        login: async (input) => {
            const res = await this.request("POST", "/api/auth/login", {
                body: input,
                skipRefresh: true,
            });
            await this.opts.onTokens?.({ accessToken: res.accessToken, refreshToken: res.refreshToken });
            return res;
        },
        logout: () => this.request("POST", "/api/auth/logout", { skipRefresh: true }),
        me: () => this.request("GET", "/api/auth/me"),
    };
    /* ------------------------------------------------------------------ */
    /* Patients                                                            */
    /* ------------------------------------------------------------------ */
    patients = {
        list: (params) => this.request("GET", "/api/patients", { query: params }),
        get: (id) => this.request("GET", `/api/patients/${id}`),
        create: (input) => this.request("POST", "/api/patients", { body: input }),
        update: (id, input) => this.request("PATCH", `/api/patients/${id}`, { body: input }),
        archive: (id) => this.request("DELETE", `/api/patients/${id}`),
        restore: (id) => this.request("POST", `/api/patients/${id}/restore`),
        records: (id) => this.request("GET", `/api/patients/${id}/records`),
        createRecord: (id, input) => this.request("POST", `/api/patients/${id}/records`, { body: input }),
        appointments: (id) => this.request("GET", `/api/patients/${id}/appointments`),
        documents: (id) => this.request("GET", `/api/patients/${id}/documents`),
        uploadDocument: (id, formData) => this.request("POST", `/api/patients/${id}/documents`, { formData }),
        deleteDocument: (patientId, documentId) => this.request("DELETE", `/api/patients/${patientId}/documents/${documentId}`),
        prescriptions: (id) => this.request("GET", `/api/patients/${id}/prescriptions`),
    };
    /* ------------------------------------------------------------------ */
    /* Appointments                                                        */
    /* ------------------------------------------------------------------ */
    appointments = {
        list: (params) => this.request("GET", "/api/appointments", { query: params }),
        create: (input) => this.request("POST", "/api/appointments", { body: input }),
        update: (id, input) => this.request("PATCH", `/api/appointments/${id}`, { body: input }),
        cancel: (id) => this.request("POST", `/api/appointments/${id}/cancel`),
    };
    public_ = {
        getAppointmentByToken: (token) => this.request("GET", `/api/public/appointments/${token}`, {
            skipRefresh: true,
        }),
        cancelByToken: (token) => this.request("POST", `/api/public/appointments/${token}/cancel`, {
            skipRefresh: true,
        }),
    };
    /* ------------------------------------------------------------------ */
    /* Prescriptions                                                       */
    /* ------------------------------------------------------------------ */
    prescriptions = {
        list: (params) => this.request("GET", "/api/prescriptions", { query: params }),
        create: (input) => this.request("POST", "/api/prescriptions", { body: input }),
    };
    /* ------------------------------------------------------------------ */
    /* Dashboard / audit / settings / users / push                         */
    /* ------------------------------------------------------------------ */
    dashboard = {
        stats: () => this.request("GET", "/api/dashboard/stats"),
    };
    insights = {
        report: () => this.request("GET", "/api/insights"),
    };
    audit = {
        list: (params) => this.request("GET", "/api/audit", { query: params }),
    };
    settings = {
        getClinic: () => this.request("GET", "/api/settings/clinic"),
        updateClinic: (input) => this.request("PUT", "/api/settings/clinic", { body: input }),
        getAvailability: () => this.request("GET", "/api/settings/availability"),
        setAvailability: (blocks) => this.request("PUT", "/api/settings/availability", { body: { blocks } }),
        emailPreviewUrl: (template) => `${this.opts.baseUrl}/api/settings/email-preview?template=${template}`,
    };
    users = {
        /** Doctor picker — available to all authenticated staff. */
        doctors: () => this.request("GET", "/api/users/doctors"),
        /** Staff directory (everyone but the caller) — for chat / mail pickers. */
        directory: () => this.request("GET", "/api/users/directory"),
        list: () => this.request("GET", "/api/users"),
        create: (input) => this.request("POST", "/api/users", { body: input }),
        update: (id, input) => this.request("PATCH", `/api/users/${id}`, { body: input }),
        remove: (id) => this.request("DELETE", `/api/users/${id}`),
    };
    push = {
        register: (input) => this.request("POST", "/api/push/register", { body: input }),
    };
    /* ------------------------------------------------------------------ */
    /* Internal chat                                                       */
    /* ------------------------------------------------------------------ */
    chat = {
        conversations: () => this.request("GET", "/api/chat/conversations"),
        start: (input) => this.request("POST", "/api/chat/conversations", { body: input }),
        messages: (conversationId) => this.request("GET", `/api/chat/conversations/${conversationId}/messages`),
        send: (conversationId, input) => this.request("POST", `/api/chat/conversations/${conversationId}/messages`, {
            body: input,
        }),
        unreadCount: () => this.request("GET", "/api/chat/unread-count"),
    };
    /* ------------------------------------------------------------------ */
    /* Internal email                                                      */
    /* ------------------------------------------------------------------ */
    mail = {
        inbox: () => this.request("GET", "/api/mail/inbox"),
        sent: () => this.request("GET", "/api/mail/sent"),
        get: (id) => this.request("GET", `/api/mail/${id}`),
        send: (input) => this.request("POST", "/api/mail", { body: input }),
        star: (id) => this.request("POST", `/api/mail/${id}/star`),
        unstar: (id) => this.request("POST", `/api/mail/${id}/unstar`),
        remove: (id) => this.request("DELETE", `/api/mail/${id}`),
        unreadCount: () => this.request("GET", "/api/mail/unread-count"),
    };
}
exports.ApiClient = ApiClient;
function createApiClient(opts) {
    return new ApiClient(opts);
}
//# sourceMappingURL=index.js.map